import { connect, FormikContextType, getIn } from 'formik';
import React, { useCallback, useMemo, useState } from 'react';
import { AccessList, useDateFns, useHasAccess } from '~/Hooks';
import { WeekDays } from '~/Models/DTO/Provider.model';
import { ExtendedLocation } from '../../../ExtendedLocation.model';
import { OpeningHoursSectionTableDesktop } from './OpeningHoursSectionTableDesktop';
import { OpeningHoursSectionTableMobile } from './OpeningHoursSectionTableMobile';
import { clone, cloneDeep } from 'lodash-es';
import { Theme, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { FormControlLabel } from '~/UIKit';
import { Switcher } from '~/UIKit/Form/Switcher';
import { useCheckForErrors } from './useCheckForErrors';
import { LocationEditCustomOpeningHoursStyles } from './OpeningHoursSectionStyles';

const defaultWeekDays = {
  monday: [],
  tuesday: [],
  wednesday: [],
  thursday: [],
  friday: [],
  saturday: [],
  sunday: [],
};

export enum TimeBlockType {
  FROM,
  TO,
}

export interface IErrorWeekdays {
  [key: string]: { isValid: boolean; type: TimeBlockType }[];
}

export const LocationEditCustomOpeningHours = connect<{}, ExtendedLocation>(
  ({ formik }: { formik: FormikContextType<ExtendedLocation> }) => {
    const { convertTimeStringToDate, formatStringToStandardTime } = useDateFns();
    const { checkForErrors } = useCheckForErrors();
    const openingHours = getIn(formik.values, 'openingHours');

    const hasWeekdays = Object.keys(openingHours?.weekdays || {}).length > 0;

    const [hasDescription, setHasDescription] = useState<boolean>(
      !hasWeekdays && !!openingHours?.description,
    );

    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
    const { t } = useTranslation();
    const { classes } = LocationEditCustomOpeningHoursStyles();
    const { hasAccess } = useHasAccess();

    //memos
    const displayWeekdays = useMemo(
      () =>
        ({
          ...defaultWeekDays,
          ...openingHours?.weekdays,
        } as WeekDays),
      [openingHours?.weekdays],
    );

    const errorWeekdays = useMemo(() => {
      const error = {
        monday: [],
        tuesday: [],
        wednesday: [],
        thursday: [],
        friday: [],
        saturday: [],
        sunday: [],
      } as IErrorWeekdays;
      Object.keys(defaultWeekDays).forEach((day: string) => {
        const sortedDay = clone(displayWeekdays[day]).sort((a, b) => {
          const aFromTime = convertTimeStringToDate(a.from);
          const bFromTime = convertTimeStringToDate(b.from);
          return aFromTime.getTime() - bFromTime.getTime();
        });

        sortedDay.forEach((timeBlock, index) => {
          if (checkForErrors(timeBlock, index > 0 ? sortedDay[index - 1] : undefined)) {
            error[day][index] = {
              isValid: false,
              type: TimeBlockType.FROM,
            };
          }
        });
      });

      return error;
    }, [displayWeekdays]);

    const weekdays = useMemo(() => {
      if (displayWeekdays) {
        return Object.keys(displayWeekdays);
      }
      return [];
    }, [displayWeekdays]);

    //methods
    const handleToggleDay = useCallback(
      (day: string, timeIndex?: number) => {
        const tempWeekDays: WeekDays = cloneDeep(displayWeekdays);

        if (displayWeekdays[day].length === 0) {
          tempWeekDays[day] = [{ from: '08:00:00', to: '10:00:00' }];
          formik.setFieldValue('openingHours.weekdays', tempWeekDays);
          return;
        }

        if (timeIndex === undefined) {
          tempWeekDays[day] = [];
        } else {
          tempWeekDays[day].splice(timeIndex, 1);
        }
        formik.setFieldValue('openingHours.weekdays', tempWeekDays);
      },
      [displayWeekdays],
    );

    const handleAddTime = useCallback(
      (day: string) => {
        const tempWeekDays: WeekDays = cloneDeep(displayWeekdays);
        const latestTimeStamp =
          tempWeekDays[day] &&
          tempWeekDays[day].reduce((prev, current) =>
            new Date(prev.to).getTime() > new Date(current.to).getTime() ? prev : current,
          );
        const nextToTimeHour = latestTimeStamp?.to?.toString().split(':')?.[0] || '00';
        const nextToTimeMinutes = `${latestTimeStamp?.to?.toString().split(':')?.[1]}` || '00';

        tempWeekDays[day].push({
          from: latestTimeStamp?.to || '00:00:00',
          to: formatStringToStandardTime(
            (Number(nextToTimeHour) + 2).toString(),
            nextToTimeMinutes,
          ),
        });
        formik.setFieldValue('openingHours.weekdays', tempWeekDays);
      },
      [displayWeekdays],
    );

    const handleCopyToAll = useCallback(() => {
      const tempWeekDays: WeekDays = cloneDeep(displayWeekdays);
      weekdays.forEach((day) => {
        tempWeekDays[day] = cloneDeep(displayWeekdays['monday']);
      });
      formik.setFieldValue('openingHours.weekdays', tempWeekDays);
    }, [displayWeekdays]);

    const handleTimeChange = useCallback(
      (day: string, timeRowIndex: number, type: 'from' | 'to', value: any) => {
        const tempWeekDays: WeekDays = cloneDeep(displayWeekdays);
        tempWeekDays[day][timeRowIndex][type] = `${new Date(value).toTimeString()}`.substring(0, 8);
        formik.setFieldValue('openingHours.weekdays', tempWeekDays);
      },
      [displayWeekdays],
    );

    const handleDescriptionToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
      const checked = e.target.checked;
      const descriptionText = checked ? t('settings.openingHours.descriptionText') : '';

      formik.setFieldValue('openingHours.description', descriptionText);
      setHasDescription(checked);
    };

    const Table = isMobile ? (
      <OpeningHoursSectionTableMobile
        errorWeekdays={errorWeekdays}
        displayWeekdays={displayWeekdays}
        weekdays={weekdays}
        onToggleDay={handleToggleDay}
        onAddTime={handleAddTime}
        onTimeChange={handleTimeChange}
        onCopyToAll={handleCopyToAll}
      />
    ) : (
      <OpeningHoursSectionTableDesktop
        errorWeekdays={errorWeekdays}
        displayWeekdays={displayWeekdays}
        weekdays={weekdays}
        onToggleDay={handleToggleDay}
        onAddTime={handleAddTime}
        onTimeChange={handleTimeChange}
        onCopyToAll={handleCopyToAll}
      />
    );

    return (
      <>
        {!hasDescription && Table}
        {hasAccess(AccessList.TEXT_DESCRIPTION_FOR_LOCATION) && (
          <div className={classes.wrapper}>
            <FormControlLabel
              control={
                <Switcher
                  className={classes.switcher}
                  checked={hasDescription}
                  onChange={handleDescriptionToggle}
                  small
                />
              }
              label={t('settings.openingHours.descriptionText')}
            />
          </div>
        )}
      </>
    );
  },
);
